import React, { useEffect, useRef, useState } from "react";
import { SearchX } from "lucide-react";

import {
  FolderSetAllConfirm,
  LeavesTable,
  LeavesToolbar,
} from "@/components/catalog/LeavesTable";
import type { Leaf } from "@/components/catalog/LeavesTable/LeafRow";
import { DashboardLayout } from "@/components/layout";
import {
  Button,
  Card,
  CardContent,
  ConfirmationModal,
  EmptyState,
  PageHeader,
} from "@/components/ui";
import { useLeafImpactQuery, useLeavesQuery } from "@/graphql/generated";
import {
  type LeafFlags,
  useLeafFlagSaves,
  useLeafSaveAnnouncement,
} from "@/hooks/useLeafFlagSaves";
import { groupLeaves } from "@/lib/leaves/leafGrouping";
import { type FolderFlag, asksBeforeRequired } from "@/lib/leaves/leafRules";

type Asking = { leaf: Leaf; settle: (save?: Promise<void>) => void };

export default function Leaves() {
  const { data, isLoading, isError, refetch, isFetching } = useLeavesQuery();
  const [text, setText] = useState("");
  const [required, setRequired] = useState(false);
  const [perStore, setPerStore] = useState(false);
  const [matchAll, setMatchAll] = useState(true);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [searchClosed, setSearchClosed] = useState<Set<string>>(new Set());
  const [asking, setAsking] = useState<Asking | null>(null);
  const impact = useLeafImpactQuery(
    { path: asking?.leaf.path ?? "" },
    { enabled: asking !== null },
  );
  const { save } = useLeafFlagSaves();
  const announcement = useLeafSaveAnnouncement();
  const leaves = data?.leaves ?? [];
  const folders = Array.from(new Set(leaves.map((leaf) => leaf.folder))).sort();
  const groups = groupLeaves(leaves, { text, required, perStore, matchAll });
  const shownCount = groups.reduce((n, g) => n + g.leaves.length, 0);
  const searching = text.trim() !== "";
  const filtered = searching || required || perStore;
  const folded = searching ? searchClosed : collapsed;
  const setFolded = searching ? setSearchClosed : setCollapsed;
  const isOpen = (f: string) => !folded.has(f);
  const toggle = (f: string) =>
    setFolded((prev) => {
      const next = new Set(prev);
      if (!next.delete(f)) next.add(f);
      return next;
    });
  const opener = useRef<HTMLElement | null>(null);
  const rememberOpener = () => {
    opener.current = document.activeElement as HTMLElement | null;
  };
  const requestRequired = (leaf: Leaf, next: boolean) =>
    asksBeforeRequired(next, leaf.usedBy)
      ? new Promise<void>((resolve, reject) => {
          rememberOpener();
          setAsking({
            leaf,
            settle: (pending = Promise.resolve()) =>
              pending.then(resolve, reject),
          });
        })
      : save(leaf, { alwaysRequired: next });
  const closeAsking = (pending?: Promise<void>) => {
    asking?.settle(pending);
    setAsking(null);
  };
  const [setAll, setSetAll] = useState<{
    folder: string;
    flag: FolderFlag;
    leaves: Leaf[];
  } | null>(null);
  const [reveal, setReveal] = useState<string | null>(null);
  const runSetAll = async (
    changing: Leaf[],
    flag: FolderFlag,
    next: boolean,
  ) => {
    for (const leaf of changing) {
      await save(leaf, { [flag]: next } as LeafFlags).catch(() => {});
    }
  };
  const showFailed = (path: string) => {
    const hidden = !groups.some((g) => g.leaves.some((l) => l.path === path));
    const folder = leaves.find((l) => l.path === path)?.folder;
    if (hidden) clearFilters();
    if (folder) {
      setCollapsed((prev) => {
        const next = new Set(prev);
        next.delete(folder);
        return next;
      });
      if (!hidden) {
        setSearchClosed((prev) => {
          const next = new Set(prev);
          next.delete(folder);
          return next;
        });
      }
    }
    setReveal(path);
  };
  useEffect(() => {
    if (asking || setAll || !opener.current) return;
    const el = opener.current;
    opener.current = null;
    const id = setTimeout(() => el.focus(), 0);
    return () => clearTimeout(id);
  }, [asking, setAll]);
  useEffect(() => {
    if (reveal === null) return;
    const row = Array.from(
      document.querySelectorAll<HTMLElement>("[data-leaf-row]"),
    ).find((el) => el.dataset.leafRow === reveal);
    row?.scrollIntoView({ block: "center" });
    row
      ?.querySelector<HTMLElement>("[data-retry]")
      ?.focus({ preventScroll: true });
    setReveal(null);
  }, [reveal]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "/" || e.ctrlKey || e.metaKey || e.altKey || e.shiftKey)
        return;
      const el = document.activeElement;
      if (
        el instanceof HTMLElement &&
        (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName))
      )
        return;
      if (document.querySelector('[role="dialog"]')) return;
      e.preventDefault();
      document.getElementById("leaves-search")?.focus();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);
  const clearFilters = () => {
    setText("");
    setSearchClosed(new Set());
    setRequired(false);
    setPerStore(false);
  };

  const body = isLoading ? (
    <p className="p-6 text-sm text-slate-500 dark:text-slate-400">
      Loading leaves…
    </p>
  ) : isError && !data ? (
    <div className="flex flex-wrap items-center gap-3 p-6 text-sm text-slate-600 dark:text-slate-300">
      Could not load leaves. Check the server is running, then try again.
      <Button
        size="sm"
        variant="outline"
        onClick={() => refetch()}
        disabled={isFetching}
      >
        Retry
      </Button>
    </div>
  ) : shownCount === 0 ? (
    <EmptyState
      icon={SearchX}
      title={filtered ? "No leaf matches" : "No leaves yet"}
      description={
        filtered
          ? "Try another word or filter."
          : "The server returned no leaves."
      }
      actionLabel={filtered ? "Clear filters" : undefined}
      onAction={filtered ? clearFilters : undefined}
    />
  ) : (
    <LeavesTable
      groups={groups}
      isOpen={isOpen}
      onToggle={toggle}
      onRequiredChange={requestRequired}
      onSetAll={(folder, flag, shown) => {
        rememberOpener();
        setSetAll({ folder, flag, leaves: shown });
      }}
    />
  );

  return (
    <DashboardLayout activePath="/catalog/leaves">
      <PageHeader
        title="Leaves"
        subtitle="Label, input type, help text, Required and Per store for every leaf"
      />
      <p className="-mt-3 mb-3 text-xs text-slate-500 dark:text-slate-400">
        Required adds a must-fill on top of each marketplace. Turning it off
        does not remove a field a marketplace itself requires.
      </p>
      <LeavesToolbar
        text={text}
        onTextChange={(value) => {
          setText(value);
          if (value.trim() === "") setSearchClosed(new Set());
        }}
        required={required}
        onRequiredChange={setRequired}
        perStore={perStore}
        onPerStoreChange={setPerStore}
        matchAll={matchAll}
        onMatchAllChange={setMatchAll}
        onExpandAll={() => setFolded(new Set())}
        onCollapseAll={() => setFolded(new Set(folders))}
        onShowFailed={showFailed}
        count={
          data
            ? filtered
              ? `${shownCount} of ${leaves.length} leaves`
              : `${leaves.length} leaves · ${folders.length} folders`
            : undefined
        }
      />
      <p aria-live="polite" className="sr-only">
        {announcement}
      </p>
      <Card className="hover:shadow-sm">
        <CardContent className="p-0">{body}</CardContent>
      </Card>
      <FolderSetAllConfirm
        target={setAll}
        onClose={() => setSetAll(null)}
        onConfirm={runSetAll}
      />
      <ConfirmationModal
        open={asking !== null}
        onOpenChange={(open) => !open && closeAsking()}
        variant="primary"
        title={`Make ${asking?.leaf.leaf ?? ""} required?`}
        description={
          impact.data
            ? `Making ${asking?.leaf.leaf} required will affect ${impact.data.leafImpact.products.toLocaleString("en-IN")} products from ${impact.data.leafImpact.sellers.toLocaleString("en-IN")} sellers. Continue?`
            : impact.isError
              ? "Could not count affected products. Continue?"
              : "Counting affected products…"
        }
        confirmLabel="Make required"
        onConfirm={() =>
          asking && closeAsking(save(asking.leaf, { alwaysRequired: true }))
        }
      />
    </DashboardLayout>
  );
}
